import React from "react";
import { getDashboardProductImageSrc } from "@/lib/productImageUrl";

/** Contrast-aware text colour for a hex background. */
function readableText(hex: string | null): string {
	if (!hex || !/^#?[0-9a-fA-F]{6}$/.test(hex.replace("#", "").padEnd(6, "0"))) return "#fff";
	const h = hex.replace("#", "");
	const r = parseInt(h.slice(0, 2), 16);
	const g = parseInt(h.slice(2, 4), 16);
	const b = parseInt(h.slice(4, 6), 16);
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
	return luminance > 0.6 ? "#1f2937" : "#fff";
}

function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * A delivery-channel badge: renders the official logo when one is set, otherwise a polished
 * brand-coloured tile with the channel initials. Consistent in both Arabic and English.
 */
export function ChannelBrandMark({
	name,
	logo,
	color,
	className,
}: {
	name: string;
	logo: string | null;
	color: string | null;
	className?: string;
}) {
	const bg = color || "#64748b";
	if (logo) {
		const src = logo.startsWith("data:") || logo.startsWith("http") ? logo : getDashboardProductImageSrc(logo);
		return (
			<span
				className={`flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-black/5 ${className ?? ""}`}
			>
				<img src={src} alt={name} className="h-full w-full object-contain" />
			</span>
		);
	}
	return (
		<span
			className={`flex shrink-0 items-center justify-center rounded-lg font-bold ${className ?? ""}`}
			style={{ backgroundColor: bg, color: readableText(bg) }}
			aria-label={name}
		>
			{initials(name)}
		</span>
	);
}
